/**
 * UpgradeConfirmModal Component
 *
 * Confirmation modal shown before starting a tenant upgrade.
 * Displays validation results, warnings, and requires acknowledgment.
 *
 * @module features/tenants/components/modals/UpgradeConfirmModal
 */

import React, { useState } from 'react';
import { AlertIcon, RefreshIcon } from '../../../../components/common/Icons';
import type { Tenant } from '../../types';
import type { UpgradeValidation } from '../../api/upgradeApi';

interface UpgradeConfirmModalProps {
  isOpen: boolean;
  tenant: Tenant | null;
  fromVersion: string;
  toVersion: string;
  validation: UpgradeValidation | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}

export const UpgradeConfirmModal: React.FC<UpgradeConfirmModalProps> = ({
  isOpen,
  tenant,
  fromVersion,
  toVersion,
  validation,
  loading,
  error,
  onClose,
  onConfirm,
}) => {
  const [acknowledged, setAcknowledged] = useState(false);

  if (!isOpen || !tenant) return null;

  // Handle both API response formats (errors array or error string)
  const errors = validation?.errors || (validation?.error ? [validation.error] : []);
  const warnings = validation?.warnings || [];
  const hasErrors = errors.length > 0;
  const canProceed = acknowledged && !hasErrors && !loading;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px', maxHeight: '80vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h2>Upgrade Tenant: {tenant.name}</h2>
          <button className="btn btn-ghost" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {/* Version info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', fontSize: '15px' }}>
            <span style={{ fontWeight: 600 }}>{fromVersion}</span>
            <span style={{ opacity: 0.5 }}>→</span>
            <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{toVersion}</span>
          </div>

          {/* Error */}
          {error && (
            <div className="alert alert-error" style={{ marginBottom: '16px', padding: '12px', borderRadius: '8px', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.5)', color: 'var(--error)' }}>
              <AlertIcon size={16} /> {error}
            </div>
          )}

          {/* Validation errors */}
          {hasErrors && (
            <div style={{ marginBottom: '16px', padding: '12px', borderRadius: '8px', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.5)' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--error)' }}>Cannot proceed:</div>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px' }}>
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}

          {/* Validation warnings */}
          {warnings.length > 0 && (
            <div style={{ marginBottom: '16px', padding: '12px', borderRadius: '8px', background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.5)' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--warning)' }}>Warnings:</div>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px' }}>
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* What will happen */}
          <div style={{ marginBottom: '20px', padding: '16px', borderRadius: '8px', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
            <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <AlertIcon size={16} /> This will:
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', lineHeight: 1.8 }}>
              <li>Create a backup of current state</li>
              <li>Stop containers temporarily</li>
              <li>Apply database migrations</li>
              <li>Start new container versions</li>
            </ul>
            {(validation?.estimated_downtime_seconds || validation?.estimated_duration_seconds) && (
              <div style={{ marginTop: '8px', fontSize: '13px', opacity: 0.8 }}>
                Estimated downtime: ~{Math.ceil((validation.estimated_downtime_seconds || validation.estimated_duration_seconds || 60) / 60)} minute{(validation.estimated_downtime_seconds || validation.estimated_duration_seconds || 0) > 60 ? 's' : ''}
              </div>
            )}
          </div>

          {/* Acknowledgment */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            I understand the upgrade process and potential downtime
          </label>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={!canProceed}>
            {loading ? <><RefreshIcon size={14} className="spin" /> Upgrading...</> : 'Start Upgrade'}
          </button>
        </div>
      </div>
    </div>
  );
};
